<template>
  <VContainer>
    <VCard
      max-width="800"
      class="mx-auto"
    >
      <VCardTitle class="d-flex align-center justify-space-between pa-4">
        <span class="text-h6">Manage Inventory Sources</span>
        <VBtn
          color="primary"
          size="small"
          @click="openAddDialog"
        >
          <VIcon
            start
            icon="ri-add-line"
          />
          Add Inventory Source
        </VBtn>
      </VCardTitle>

      <VTable
        density="compact"
        class="text-no-wrap"
      >
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Alias Name</th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td
              colspan="4"
              class="text-center pa-4"
            >
              <VProgressCircular
                indeterminate
                color="primary"
              />
            </td>
          </tr>
          <tr v-else-if="inventorySources.length === 0">
            <td
              colspan="4"
              class="text-center pa-4 text-grey"
            >
              No inventory sources found.
            </td>
          </tr>
          <tr
            v-for="(source, index) in inventorySources"
            :key="source.id"
          >
            <td>{{ index + 1 }}</td>
            <td>{{ source.name }}</td>
            <td>
              <VChip
                color="info"
                size="small"
              >
                {{ source.aliasName }}
              </VChip>
            </td>
            <td class="text-center">
              <VBtn
                icon
                size="small"
                variant="text"
                @click="openEditDialog(source)"
              >
                <VIcon
                  icon="ri-pencil-line"
                  size="18"
                />
              </VBtn>
              <VBtn
                icon
                size="small"
                variant="text"
                color="error"
                @click="openDeleteDialog(source)"
              >
                <VIcon
                  icon="ri-delete-bin-5-line"
                  size="18"
                />
              </VBtn>
            </td>
          </tr>
        </tbody>
      </VTable>
    </VCard>

    <!-- Add/Edit Dialog -->
    <VDialog
      v-model="showFormDialog"
      max-width="500"
      persistent
    >
      <VCard :title="isEditMode ? 'Edit Inventory Source' : 'Add Inventory Source'">
        <VCardText>
          <VForm ref="formRef">
            <VRow>
              <VCol cols="12">
                <VTextField
                  v-model="formData.name"
                  label="Name *"
                  placeholder="e.g., hungry_hub"
                  :rules="[v => !!v || 'Name is required']"
                />
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-model="formData.aliasName"
                  label="Alias Name *"
                  placeholder="e.g., HH"
                  :rules="[v => !!v || 'Alias name is required']"
                />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn @click="closeFormDialog">Cancel</VBtn>
          <VBtn
            color="primary"
            :loading="isSaving"
            @click="saveInventorySource"
          >
            {{ isEditMode ? 'Update' : 'Create' }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Delete Confirmation Dialog -->
    <VDialog
      v-model="showDeleteDialog"
      max-width="400"
    >
      <VCard>
        <VCardTitle class="text-h6">Confirm Delete</VCardTitle>
        <VCardText>
          Are you sure you want to delete
          <strong>{{ deleteTarget?.aliasName }}</strong> ({{ deleteTarget?.name }})? This will also remove all
          environment and test case associations.
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn @click="showDeleteDialog = false">Cancel</VBtn>
          <VBtn
            color="error"
            :loading="isDeleting"
            @click="deleteInventorySource"
          >
            Delete
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Snackbar -->
    <VSnackbar
      v-model="isShowSnackBar"
      multi-line
      :globalTop="true"
      :color="alertType"
      dismissible
      class="mb-4"
      @click:close="clearAlert"
    >
      {{ alertMessage }}
    </VSnackbar>
  </VContainer>
</template>

<script setup lang="ts">
import { useAlert } from '@/composables/useAlert'
import { onMounted, ref } from 'vue'

const configStore = useConfigStore()
const { alertMessage, alertType, isShowSnackBar, showAlert, clearAlert } = useAlert()

interface InventorySource {
  id: number
  name: string
  aliasName: string
}

const inventorySources = ref<InventorySource[]>([])
const isLoading = ref(false)
const isSaving = ref(false)
const isDeleting = ref(false)

// Form dialog
const showFormDialog = ref(false)
const isEditMode = ref(false)
const formRef = ref()
const formData = ref({
  id: null as number | null,
  name: '',
  aliasName: '',
})

// Delete dialog
const showDeleteDialog = ref(false)
const deleteTarget = ref<InventorySource | null>(null)

async function fetchInventorySources() {
  isLoading.value = true
  try {
    const response = await $fetch<{ success: boolean; data?: InventorySource[]; message?: string }>(
      '/api/setting/inventory-sources',
      { method: 'GET' },
    )

    if (response.success) {
      inventorySources.value = response.data ?? []
    } else {
      showAlert(response.message || 'Failed to fetch inventory sources', 'error')
    }
  } catch (err: any) {
    showAlert(err.message || 'Failed to fetch inventory sources', 'error')
  } finally {
    isLoading.value = false
  }
}

function openAddDialog() {
  isEditMode.value = false
  formData.value = { id: null, name: '', aliasName: '' }
  showFormDialog.value = true
}

function openEditDialog(source: InventorySource) {
  isEditMode.value = true
  formData.value = { id: source.id, name: source.name, aliasName: source.aliasName }
  showFormDialog.value = true
}

function closeFormDialog() {
  showFormDialog.value = false
  formData.value = { id: null, name: '', aliasName: '' }
}

function openDeleteDialog(source: InventorySource) {
  deleteTarget.value = source
  showDeleteDialog.value = true
}

async function saveInventorySource() {
  const { valid } = await formRef.value?.validate()
  if (!valid) return

  isSaving.value = true
  try {
    const method = isEditMode.value ? 'PUT' : 'POST'
    const response = await $fetch<{ success: boolean; message?: string }>('/api/setting/inventory-sources', {
      method,
      body: formData.value,
    })

    if (response.success) {
      showAlert(response.message || 'Saved successfully!', 'success')
      closeFormDialog()
      await fetchInventorySources()
      // Refresh the config store so other pages see the update
      await configStore.fetchDataInventorySources()
    } else {
      showAlert(response.message || 'Failed to save', 'error')
    }
  } catch (err: any) {
    showAlert(err.message || 'Failed to save', 'error')
  } finally {
    isSaving.value = false
    setTimeout(() => clearAlert(), 3000)
  }
}

async function deleteInventorySource() {
  if (!deleteTarget.value) return

  isDeleting.value = true
  try {
    const response = await $fetch<{ success: boolean; message?: string }>('/api/setting/inventory-sources', {
      method: 'DELETE',
      body: { id: deleteTarget.value.id },
    })

    if (response.success) {
      showAlert(response.message || 'Deleted successfully!', 'success')
      showDeleteDialog.value = false
      deleteTarget.value = null
      await fetchInventorySources()
      await configStore.fetchDataInventorySources()
    } else {
      showAlert(response.message || 'Failed to delete', 'error')
    }
  } catch (err: any) {
    showAlert(err.message || 'Failed to delete', 'error')
  } finally {
    isDeleting.value = false
    setTimeout(() => clearAlert(), 3000)
  }
}

onMounted(async () => {
  await fetchInventorySources()
})
</script>
